// @unocss-include

import type { ToolInvocation } from 'ai';

function cleanPath(path: string) {
  return path.split('/project/')[1];
}

export const useToolCallText = (toolInvocation: ToolInvocation, isPending: boolean) => {
  const { toolName, args } = toolInvocation;

  // helper to convert to present continuous tense for known verbs
  function toPresentContinuous(title: string) {
    const map: Record<string, string> = {
      Read: 'Reading',
      Edit: 'Editing',
      Write: 'Writing',
      'List files': 'Listing files',
      Search: 'Searching',
      'Exit plan mode': 'Exiting plan mode',
      'Setup database': 'Setting up database',
      'List database tables': 'Listing database tables',
      'List database extensions': 'Listing database extensions',
      'Execute SQL': 'Executing SQL',
      'List database migrations': 'Listing database migrations',
      'Apply database migration': 'Applying database migration',
      'Deploy edge function': 'Deploying edge function',
      'List edge functions': 'Listing edge functions',
      'Update plan': 'Updating plan',
      'Initialize plan': 'Initializing plan',
      'Plan completed': 'Completing plan',
      'Web request': 'Requesting web page',
      'Run command': 'Running command',
      'Read errors': 'Reading errors',
    };

    const prefixMap: [string, string][] = [
      ['Create', 'Creating'],
      ['Apply', 'Applying'],
      ['Build', 'Building'],
      ['Set up', 'Setting up'],
      ['Style', 'Styling'],
      ['Implement', 'Implementing'],
      ['Add', 'Adding'],
      ['Update', 'Updating'],
      ['Initialize', 'Initializing'],
      ['Run', 'Running'],
      ['List', 'Listing'],
    ];

    if (map[title]) {
      return map[title];
    }

    for (const [prefix, replacement] of prefixMap) {
      if (title.startsWith(prefix)) {
        return replacement + title.slice(prefix.length);
      }
    }

    if (/ing$/.test(title)) {
      return title;
    }

    return title;
  }

  // helper to convert to past tense for known verbs
  function toPastTense(title: string) {
    const map: Record<string, string> = {
      Read: 'Read',
      Edit: 'Edited',
      Write: 'Wrote',
      'List files': 'Listed files',
      Search: 'Searched',
      'Exit plan mode': 'Exited plan mode',
      'Setup database': 'Set up database',
      'List database tables': 'Listed database tables',
      'List database extensions': 'Listed database extensions',
      'Execute SQL': 'Executed SQL',
      'List database migrations': 'Listed database migrations',
      'Apply database migration': 'Applied database migration',
      'Deploy edge function': 'Deployed edge function',
      'List edge functions': 'Listed edge functions',
      'Update plan': 'Updated plan',
      'Initialize plan': 'Initialized plan',
      'Plan completed': 'Plan completed',
      'Web request': 'Web request',
      'Run command': 'Ran command',
    };

    const prefixMap: [string, string][] = [
      ['Create', 'Created'],
      ['Apply', 'Applied'],
      ['Build', 'Built'],
      ['Set up', 'Set up'],
      ['Implement', 'Implemented'],
      ['Add', 'Added'],
      ['Style', 'Styled'],
      ['Update', 'Updated'],
      ['Initialize', 'Initialized'],
      ['Run', 'Ran'],
      ['List', 'Listed'],
    ];

    if (map[title]) {
      return map[title];
    }

    for (const [prefix, replacement] of prefixMap) {
      if (title.startsWith(prefix)) {
        return replacement + title.slice(prefix.length);
      }
    }

    if (/ed$/.test(title)) {
      return title;
    }

    return title;
  }

  function tense(title: string) {
    return isPending ? toPresentContinuous(title) : toPastTense(title);
  }

  if (toolName === 'Read' && args.file_path) {
    return {
      icon: 'i-heroicons-outline:eye',
      title: tense('Read'),
      value: cleanPath(args.file_path),
    };
  }

  if (toolName === 'MultiEdit' && args.file_path) {
    return {
      icon: 'i-heroicons-outline:pencil-square',
      title: tense('Edit'),
      value: cleanPath(args.file_path),
    };
  }

  if (/Write|Edit/.test(toolName) && args.file_path) {
    return {
      icon: 'i-heroicons-outline:pencil-square',
      title: tense(toolName),
      value: cleanPath(args.file_path),
    };
  }

  if (toolName === 'LS' && args.path) {
    return {
      icon: 'i-heroicons-outline:search',
      title: tense('List files'),
      value: cleanPath(args.path) || '/',
    };
  }

  if (toolName === 'Glob') {
    return {
      icon: 'i-heroicons-outline:search',
      title: tense('Search'),
      value: args.pattern,
    };
  }

  if (toolName === 'Grep') {
    return {
      icon: 'i-heroicons-outline:search',
      title: tense('Search'),
      value: args.pattern,
    };
  }

  if (toolName === 'ExitPlanMode') {
    return {
      icon: 'i-heroicons-outline:arrow-right-on-rectangle',
      title: tense('Exit plan mode'),
    };
  }

  if (toolName === 'mcp__supabase__setup_database') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('Setup database'),
    };
  }

  if (toolName === 'mcp__supabase__list_tables') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('List database tables'),
    };
  }

  if (toolName === 'mcp__supabase__list_extensions') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('List database extensions'),
    };
  }

  if (toolName === 'mcp__supabase__execute_sql') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('Execute SQL'),
    };
  }

  if (toolName === 'mcp__supabase__list_migrations') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('List database migrations'),
    };
  }

  if (toolName === 'mcp__supabase__apply_migration') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('Apply database migration'),
    };
  }

  if (toolName === 'mcp__supabase__deploy_edge_function') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('Deploy edge function'),
    };
  }

  if (toolName === 'mcp__supabase__list_edge_functions') {
    return {
      icon: 'i-heroicons-outline:database',
      title: tense('List edge functions'),
    };
  }

  if (toolName === 'TodoWrite') {
    let title = 'Update plan';
    let icon = 'i-heroicons-outline:list-bullet';

    if (args.todos.every((todo: any) => todo.status === 'pending')) {
      title = 'Initialize plan';
    } else {
      const inProgressTodo = args.todos.find((todo: any) => todo.status === 'in_progress');

      if (inProgressTodo) {
        title = `${inProgressTodo.content}`;
        icon = 'i-heroicons-outline:check';
      } else if (args.todos.every((todo: any) => todo.status === 'completed' || todo.status === 'cancelled')) {
        title = 'Plan completed';
        icon = 'i-bolt:check-double';
      }
    }

    return {
      icon,
      title: tense(title),
    };
  }

  if (toolName === 'WebFetch') {
    return {
      icon: 'i-heroicons-outline:globe-alt',
      title: tense('Web request'),
    };
  }

  if (toolName === 'Bash') {
    const title = args.description ? args.description : 'Run command';

    return {
      icon: 'i-heroicons-outline:command-line',
      title: tense(title),
    };
  }

  if (toolName === 'mcp__diagnostics__read_errors') {
    return {
      icon: 'i-heroicons-outline:wrench',
      title: tense('Read errors'),
    };
  }

  return {
    icon: 'i-heroicons-outline:wrench',
    title: tense(toolName),
  };
};
